import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
// Direct-path icon imports, like ReferralSourcesPage: neither TableView nor
// PictureAsPdf is registered in `test-utils/mui-icons-barrel-redirect.ts`, so
// pulling them through the `@mui/icons-material` barrel renders `undefined`
// under vitest until someone adds an entry there.
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  adminApi,
  InactiveUsersExportFormat,
  InactiveUsersReport,
  InactiveUserRow,
} from '../services/api';
import { downloadBase64File } from '../utils/download-file';
import { useDebounce } from '../hooks/useDebounce';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';

const ROWS_PER_PAGE = 25;

// The inactivity thresholds the tabs filter on (days). 30 is the default — the
// most actionable churn bucket.
const THRESHOLDS = [7, 14, 30] as const;
const DEFAULT_THRESHOLD = 30;

function userDisplayName(row: InactiveUserRow): string {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return name || row.email || row.userId;
}

/** Client mirror of the backend predicate: never-run OR last run ≥ days ago. */
function isInactiveForDays(row: InactiveUserRow, days: number): boolean {
  if (row.neverRun) return true;
  return row.daysSinceLastRun !== null && row.daysSinceLastRun >= days;
}

function lastRunLabel(row: InactiveUserRow): string {
  if (row.neverRun || row.daysSinceLastRun === null) return 'Never';
  if (row.daysSinceLastRun === 0) return 'Today';
  return `${row.daysSinceLastRun}d ago`;
}

const InactiveUsersPage: React.FC = () => {
  const [report, setReport] = useState<InactiveUsersReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number>(DEFAULT_THRESHOLD);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearch = useDebounce(searchTerm);
  // Which format is in flight, not a boolean: one button shows "Preparing…"
  // while the other disables (each export is a full pool scan server-side).
  const [exporting, setExporting] = useState<InactiveUsersExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Re-fetch whenever the debounced term changes. The search runs SERVER-side
  // (before the top-N cap), so a match outside the capped page is still found —
  // filtering the loaded rows here could not do that (Trello #607).
  // NB: `cancelled` flag only, no "already started" ref — under StrictMode a ref
  // guard would skip the second mount's fetch while the first is cancelled.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getInactiveUsers(debouncedSearch || undefined)
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load inactive users.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load inactive users.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // A new result set invalidates the current page offset.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const summary = report?.summary;

  const inactiveCountFor = (days: number): number => {
    if (!summary) return 0;
    if (days === 7) return summary.inactive7;
    if (days === 14) return summary.inactive14;
    return summary.inactive30;
  };

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'total', caption: 'Total users', value: (summary?.totalUsers ?? 0).toLocaleString() },
    { key: 'never', caption: 'Never run a job', value: (summary?.neverRun ?? 0).toLocaleString() },
    { key: 'inactive30', caption: 'Inactive ≥30 days', value: (summary?.inactive30 ?? 0).toLocaleString() },
  ];

  const filtered = useMemo(
    () => (report?.users ?? []).filter((u) => isInactiveForDays(u, threshold)),
    [report, threshold],
  );

  // Client-side pagination over the (already top-N-capped) filtered set. Clamp
  // the page in render so changing the threshold tab never shows a blank page.
  const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
  const safePage = Math.min(page, maxPage);
  const pagedRows = filtered.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const loginTrackingActive = summary?.loginTrackingActive ?? false;
  const searchActive = debouncedSearch.length > 0;

  // How many users the FILE will contain. The bucket counts are computed
  // server-side over the whole searched population BEFORE the top-N cap, so
  // this is the export's real size — and on a truncated view it is deliberately
  // larger than `filtered.length`, which is the whole point of the feature.
  const exportCount = inactiveCountFor(threshold);

  const handleExport = async (format: InactiveUsersExportFormat): Promise<void> => {
    if (exporting) return;
    setExporting(format);
    setExportError(null);
    try {
      // `debouncedSearch`, not `searchTerm`: it is what the table was rendered
      // from, so mid-typing the file still matches what is on screen.
      const res = await adminApi.exportInactiveUsers(format, {
        search: debouncedSearch || undefined,
        threshold,
      });
      const { fileBase64, filename, contentType } = res.data ?? {};
      if (res.success && !fileBase64) {
        // The old backend ignores `?format=` and returns the JSON report, so a
        // frontend that reaches prod first degrades to a message rather than
        // handing the admin an empty download.
        throw new Error('Export is not available yet — this environment has not been updated.');
      }
      if (!res.success || !fileBase64 || !filename) throw new Error('No file returned');
      downloadBase64File(fileBase64, filename, contentType || 'application/octet-stream');
    } catch (err: any) {
      setExportError(err?.response?.data?.message || err?.message || 'Failed to download the list.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Inactive Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users on your stack who haven't run a job recently — to spot churn risk and re-engage
            brokers before they lapse. "Inactive ≥N days" includes users who have never run a job.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TableViewIcon />}
            disabled={loading || !report || exporting !== null || exportCount === 0}
            onClick={() => void handleExport('xlsx')}
            data-testid="inactive-export-xlsx"
          >
            {exporting === 'xlsx' ? 'Preparing…' : `Excel (${exportCount.toLocaleString()})`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            disabled={loading || !report || exporting !== null || exportCount === 0}
            onClick={() => void handleExport('pdf')}
            data-testid="inactive-export-pdf"
          >
            {exporting === 'pdf' ? 'Preparing…' : `PDF (${exportCount.toLocaleString()})`}
          </Button>
        </Stack>
      </Box>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="inactive-export-error">
          {exportError}
        </Alert>
      )}

      <PageHelp {...PAGE_HELP.inactiveUsers} />

      {/* Always mounted — if this unmounted while a search re-fetches, the box
          would lose focus mid-typing. */}
      <TextField
        size="small"
        fullWidth
        placeholder="Search by name, email or organisation..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, maxWidth: 480 }}
        inputProps={{ 'data-testid': 'inactive-search-input' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* What the Excel / PDF buttons will actually contain. With no
          organisation picker on this page, an unsearched export is EVERY user
          on the stack — every firm's people in one file — and the stated use is
          to email it to a client. Say so before they click, not after. */}
      {!loading && !error && report && (
        <Typography
          variant="caption"
          color={searchActive ? 'text.secondary' : 'warning.main'}
          sx={{ display: 'block', mb: 2 }}
          data-testid="inactive-export-scope-note"
        >
          {searchActive
            ? `Excel / PDF exports all ${exportCount.toLocaleString()} user(s) matching "${debouncedSearch}" — including any beyond the ${filtered.length.toLocaleString()} shown below.`
            : `No search applied — Excel / PDF exports all ${exportCount.toLocaleString()} user(s) on this stack. Search for the client's organisation first to scope the list.`}
        </Typography>
      )}

      {searchActive && !loading && !error && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2 }}
          data-testid="search-scope-note"
        >
          Searched every user on this stack for &quot;{debouncedSearch}&quot; — the counts below
          describe the matches, not the whole stack.
        </Typography>
      )}

      {loading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!error && report && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="summary-cards"
          >
            {cards.map((card) => (
              <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.caption}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mt: 1 }}
                    data-testid={`card-${card.key}-value`}
                  >
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {!loginTrackingActive && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="login-tracking-notice">
              Login tracking isn't live yet — the "Last login" column will populate once login
              instrumentation ships and data accrues. Inactivity is based on last run for now.
            </Alert>
          )}

          {/* Dimmed rather than unmounted while a search re-fetches, so the
              table doesn't flash away under the user between keystrokes. */}
          <Paper variant="outlined" sx={{ opacity: loading ? 0.5 : 1 }}>
            <Tabs
              value={threshold}
              onChange={(_e, v) => setThreshold(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
              data-testid="threshold-tabs"
            >
              {THRESHOLDS.map((d) => (
                <Tab
                  key={d}
                  value={d}
                  label={`Inactive ≥${d}d (${inactiveCountFor(d).toLocaleString()})`}
                  data-testid={`tab-${d}`}
                />
              ))}
            </Tabs>
            <TableContainer>
              <Table size="small" data-testid="inactive-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last run</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last login</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchActive
                            ? `No users matching "${debouncedSearch}" are inactive for ≥${threshold} days. Try a wider tab, or a different spelling.`
                            : `No users inactive for ≥${threshold} days on this stack. 🎉`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedRows.map((row) => (
                    <TableRow key={row.userId} hover data-testid={`row-${row.userId}`}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {userDisplayName(row)}
                        </Typography>
                        {row.email && userDisplayName(row) !== row.email && (
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Name first — the organizationId is opaque to an admin
                            scanning this list. Falls back to the id when the
                            org row has no name (or names couldn't be loaded). */}
                        <Typography variant="body2">
                          {row.organizationName || row.organizationId || '—'}
                        </Typography>
                        {row.organizationName && row.organizationId && (
                          <Typography variant="caption" color="text.secondary">
                            {row.organizationId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.neverRun ? (
                          <Tooltip title="No completed job on record">
                            <Chip size="small" label="Never" color="warning" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={row.lastRunAt ? new Date(row.lastRunAt).toLocaleString() : ''}>
                            <Typography variant="body2">{lastRunLabel(row)}</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {!loginTrackingActive ? (
                          <Typography variant="caption" color="text.secondary">
                            not tracked yet
                          </Typography>
                        ) : row.daysSinceLastLogin === null ? (
                          <Typography variant="body2">Never</Typography>
                        ) : (
                          <Tooltip title={row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : ''}>
                            <Typography variant="body2">
                              {row.daysSinceLastLogin === 0 ? 'Today' : `${row.daysSinceLastLogin}d ago`}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filtered.length > ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={safePage}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                data-testid="inactive-pagination"
              />
            )}
          </Paper>

          {report.truncated && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="truncation-note">
              Showing the {(report.users?.length ?? 0).toLocaleString()} most-inactive users of{' '}
              {(report.total ?? 0).toLocaleString()} total{searchActive ? ' matching your search' : ' on this stack'}
              . {searchActive
                ? 'Narrow the search term to see them all.'
                : 'Narrow by a tighter threshold to see the most at-risk first.'}{' '}
              The Excel / PDF export is not capped — it contains all of them.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {filtered.length.toLocaleString()} user(s) shown · generated{' '}
            {new Date(report.generatedAt).toLocaleString()}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default InactiveUsersPage;
